import { normalizeCnCode } from 'modules/Common/utils/formatCnCode';

/**
 * Utility for standardized CN goods filtering with space normalization
 * Works with react-select's filterOption callback
 *
 * @param {Object} option - The react-select option object
 * @param {string} inputValue - The search input value
 * @returns {boolean} - Whether the option matches the search
 */
export function createCnGoodFilterOption(option, inputValue) {
  if (!inputValue) return true;
  if (!option) return false;

  const searchText = inputValue.toLowerCase().trim();
  if (!searchText) return true;

  // Normalize by removing all spaces, dots, and hyphens
  const normalizedSearch = normalizeCnCode(searchText).toLowerCase();

  // Search in label (includes both code and name)
  if (option.label) {
    const normalizedLabel = normalizeCnCode(option.label).toLowerCase();
    if (
      normalizedLabel.includes(normalizedSearch) ||
      option.label.toLowerCase().includes(searchText)
    ) {
      return true;
    }
  }

  // Search in cnGood object if available (from CgCnGoodSelector)
  if (option.cnGood || option.data?.cnGood) {
    const cnGood = option.cnGood || option.data?.cnGood;

    const idMatch =
      cnGood._id &&
      (cnGood._id.toLowerCase().includes(searchText) ||
        normalizeCnCode(cnGood._id).toLowerCase().includes(normalizedSearch));

    const cnCodeMatch =
      cnGood.cnCode &&
      (cnGood.cnCode.toLowerCase().includes(searchText) ||
        normalizeCnCode(cnGood.cnCode).toLowerCase().includes(normalizedSearch));

    return (
      idMatch ||
      cnCodeMatch ||
      (cnGood.name && cnGood.name.toLowerCase().includes(searchText)) ||
      (cnGood.goodType && cnGood.goodType.toLowerCase().includes(searchText)) ||
      (cnGood.aggregatedGoodsCategory &&
        cnGood.aggregatedGoodsCategory.toLowerCase().includes(searchText)) ||
      // Localized category label (additive, non-English responses only) —
      // search must match what the user sees on screen.
      (cnGood.aggregatedGoodsCategoryLabel &&
        cnGood.aggregatedGoodsCategoryLabel.toLowerCase().includes(searchText))
    );
  }

  // Fallback to value search with space normalization. Coerce to string
  // because callers may pass numeric values (e.g. an index used as the
  // option key) and Number#toLowerCase doesn't exist.
  if (option.value != null) {
    const valueStr = String(option.value).toLowerCase();
    return (
      valueStr.includes(searchText) ||
      normalizeCnCode(valueStr).toLowerCase().includes(normalizedSearch)
    );
  }

  return false;
}
